/**
 * InvestorAuthModal — three-state modal (login / register / forgot password).
 *
 * Used on /invest, /investor-dashboard and any protected investor route.
 * Small, single-file, no external UI libs beyond shadcn Input/Button that are
 * already in the codebase.
 */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Mail, Lock, User, Loader2, ArrowLeft, CheckCircle2, Phone, KeyRound } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useInvestorAuth } from '../contexts/InvestorAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODE_LOGIN = 'login';
const MODE_REGISTER = 'register';
const MODE_FORGOT = 'forgot';
const MODE_OTP = 'otp';   // email-OTP gate before finalising registration

export const InvestorAuthModal = ({ open, onClose, initialMode = MODE_LOGIN, onAuthenticated }) => {
  const { login, register, forgotPassword } = useInvestorAuth();
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', otp: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [otpSentTo, setOtpSentTo] = useState('');

  // Lock background scroll while modal is open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const reset = () => {
    setError('');
    setSuccess('');
    setForm({ name: '', email: '', password: '', phone: '', otp: '' });
    setOtpSentTo('');
  };

  const switchMode = (next) => {
    reset();
    setMode(next);
  };

  const close = () => {
    reset();
    setMode(initialMode);
    onClose?.();
  };

  const requestEmailOtp = async () => {
    setError('');
    setSuccess('');
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/email-otp/send`, { email: form.email.trim() });
      setOtpSentTo(form.email.trim());
      setMode(MODE_OTP);
      // Dev fallback: backend echoes OTP when email fails so the user isn't blocked.
      if (data?.dev_otp) setSuccess(`OTP sent. (Dev fallback: ${data.dev_otp})`);
      else setSuccess(`4-digit code sent to ${form.email.trim()}. Check your inbox (and spam).`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send verification code. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setBusy(true);
    try {
      if (mode === MODE_LOGIN) {
        const res = await login(form.email.trim(), form.password);
        if (!res.success) setError(res.error);
        else { onAuthenticated?.(); close(); }
      } else if (mode === MODE_REGISTER) {
        // Try to trigger email OTP; if the mail can't be sent we still let the
        // user create an account (email marked unverified) so signup is never
        // blocked by SMTP outages.
        if (!form.name.trim() || form.password.length < 6) {
          setError('Please enter your name and a password of at least 6 characters.');
          setBusy(false);
          return;
        }
        setBusy(false);
        await requestEmailOtp();
        return;
      } else if (mode === MODE_OTP) {
        // Verify OTP then register (backend accepts empty OTP as unverified).
        const res = await register({
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          phone: form.phone.trim() || undefined,
          otp: form.otp.trim(),
        });
        if (!res.success) setError(res.error);
        else { onAuthenticated?.(); close(); }
      } else if (mode === MODE_FORGOT) {
        const res = await forgotPassword(form.email.trim());
        if (!res.success) setError(res.error);
        else setSuccess(res.message || 'If an account exists, a reset link has been sent.');
      }
    } finally {
      setBusy(false);
    }
  };

  const title = {
    [MODE_LOGIN]: 'Welcome back',
    [MODE_REGISTER]: 'Create your investor account',
    [MODE_FORGOT]: 'Reset your password',
    [MODE_OTP]: 'Verify your email',
  }[mode];

  const subtitle = {
    [MODE_LOGIN]: 'Log in to view your fractional-land portfolio.',
    [MODE_REGISTER]: 'Save your waitlist spot & unlock the investor dashboard.',
    [MODE_FORGOT]: "Enter your email and we'll send you a one-time reset link.",
    [MODE_OTP]: `We sent a 4-digit code to ${otpSentTo || 'your email'}.`,
  }[mode];

  const modalNode = (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto"
      onClick={close}
      data-testid="investor-auth-modal"
    >
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header band */}
        <div className="bg-gradient-to-br from-emerald-700 via-emerald-800 to-slate-900 text-white px-6 pt-6 pb-8">
          <div className="flex justify-between items-start">
            <div className="text-[10px] tracking-[3px] uppercase text-amber-300 font-bold">
              Acre Mitra &middot; Customer Portal
            </div>
            <button
              onClick={close}
              className="text-white/70 hover:text-white transition"
              aria-label="Close"
              data-testid="investor-auth-close"
            >
              <X size={20} />
            </button>
          </div>
          <h2 className="text-2xl font-bold mt-3">{title}</h2>
          <p className="text-emerald-100/90 text-sm mt-1 leading-snug">{subtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {mode === MODE_REGISTER && (
            <>
              <div>
                <Label htmlFor="inv-name" className="text-xs font-semibold text-slate-600">Full name</Label>
                <div className="relative mt-1">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input id="inv-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="pl-9" placeholder="Rajat Sen" required data-testid="investor-name-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="inv-phone" className="text-xs font-semibold text-slate-600">Phone (optional)</Label>
                <div className="relative mt-1">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input id="inv-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="pl-9" placeholder="+91 9830011223" data-testid="investor-phone-input" />
                </div>
              </div>
            </>
          )}

          {mode !== MODE_OTP && (
            <div>
              <Label htmlFor="inv-email" className="text-xs font-semibold text-slate-600">Email</Label>
              <div className="relative mt-1">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="inv-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="pl-9" placeholder="you@company.com" autoComplete="email" required data-testid="investor-email-input" />
              </div>
            </div>
          )}

          {(mode === MODE_LOGIN || mode === MODE_REGISTER) && (
            <div>
              <div className="flex justify-between items-baseline">
                <Label htmlFor="inv-pass" className="text-xs font-semibold text-slate-600">Password</Label>
                {mode === MODE_LOGIN && (
                  <button type="button" onClick={() => switchMode(MODE_FORGOT)} className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900" data-testid="investor-forgot-link">Forgot?</button>
                )}
              </div>
              <div className="relative mt-1">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="inv-pass" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="pl-9" placeholder={mode === MODE_REGISTER ? 'Minimum 6 characters' : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'} autoComplete={mode === MODE_REGISTER ? 'new-password' : 'current-password'} required minLength={6} data-testid="investor-password-input" />
              </div>
            </div>
          )}

          {mode === MODE_OTP && (
            <div>
              <Label htmlFor="inv-otp" className="text-xs font-semibold text-slate-600">4-digit code</Label>
              <div className="relative mt-1">
                <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="inv-otp"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  value={form.otp}
                  onChange={(e) => setForm({ ...form, otp: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  className="pl-9 tracking-[0.6em] text-center text-lg font-black"
                  placeholder="0000"
                  autoFocus
                  required
                  data-testid="investor-otp-input"
                />
              </div>
              <div className="mt-2 flex justify-between items-center text-[11px]">
                <button type="button" onClick={requestEmailOtp} disabled={busy} className="text-emerald-700 hover:text-emerald-900 font-semibold disabled:opacity-40" data-testid="investor-otp-resend">Resend code</button>
                <span className="text-slate-400">Code expires in 10 minutes</span>
              </div>
              {/* Fallback: register without OTP if the email never arrives. Marks email as unverified. */}
              <button
                type="button"
                onClick={async () => {
                  setError('');
                  setBusy(true);
                  const res = await register({
                    name: form.name.trim(),
                    email: form.email.trim(),
                    password: form.password,
                    phone: form.phone.trim() || undefined,
                    otp: '',
                  });
                  setBusy(false);
                  if (!res.success) setError(res.error);
                  else { onAuthenticated?.(); close(); }
                }}
                disabled={busy}
                className="mt-3 w-full text-[12px] font-semibold text-slate-600 hover:text-emerald-700 underline underline-offset-2 disabled:opacity-40"
                data-testid="investor-otp-skip"
              >
                Didn&rsquo;t get the code? Continue without email verification →
              </button>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="investor-auth-error">{error}</div>
          )}
          {success && (
            <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex gap-2 items-start" data-testid="investor-auth-success">
              <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
              <span>{success}</span>
            </div>
          )}

          <Button type="submit" disabled={busy || (mode === MODE_OTP && form.otp.length !== 4)} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-bold h-11" data-testid="investor-auth-submit">
            {busy && <Loader2 size={16} className="animate-spin mr-2" />}
            {mode === MODE_LOGIN && 'Log in'}
            {mode === MODE_REGISTER && 'Continue \u2192 Verify email'}
            {mode === MODE_OTP && 'Verify & Create account'}
            {mode === MODE_FORGOT && 'Send reset link'}
          </Button>

          <div className="pt-2 text-center text-sm text-slate-600">
            {mode === MODE_LOGIN && (
              <>New to Acre Mitra?{' '}<button type="button" onClick={() => switchMode(MODE_REGISTER)} className="text-emerald-700 hover:text-emerald-900 font-semibold" data-testid="investor-switch-register">Create an account</button></>
            )}
            {mode === MODE_REGISTER && (
              <>Already have an account?{' '}<button type="button" onClick={() => switchMode(MODE_LOGIN)} className="text-emerald-700 hover:text-emerald-900 font-semibold" data-testid="investor-switch-login">Log in</button></>
            )}
            {mode === MODE_OTP && (
              <button type="button" onClick={() => switchMode(MODE_REGISTER)} className="text-slate-500 hover:text-slate-800 font-semibold inline-flex items-center gap-1" data-testid="investor-switch-back-register"><ArrowLeft size={13} /> Change email</button>
            )}
            {mode === MODE_FORGOT && (
              <button type="button" onClick={() => switchMode(MODE_LOGIN)} className="text-slate-500 hover:text-slate-800 font-semibold inline-flex items-center gap-1" data-testid="investor-switch-back-login"><ArrowLeft size={13} /> Back to log in</button>
            )}
          </div>
        </form>
      </div>
    </div>
  );

  // Portal into document.body so the modal is not constrained by any
  // ancestor with transform / overflow / stacking context (fixes the
  // "modal renders under header / not centered" bug in Safari).
  return typeof document !== 'undefined' ? createPortal(modalNode, document.body) : modalNode;
};

export default InvestorAuthModal;
